/** The submission pack: what a filing hands the regulator, each answer with its receipt.
 *
 * **EmitIQ prepares; the company submits.** Nothing in this file sends anything anywhere, and the
 * response carries no state called "submitted". The pack is read from the regime's own rows
 * (`regime_submission_row`), so nothing here names a regulator: a second market's pack arrives as
 * rows and this file does not change.
 *
 * The one write drafts the words of a statement the regime accepts in place of a filing. It needs a
 * person to confirm the **fact** the statement asserts about the company (`confirmed`), because
 * EmitIQ cannot see the company's past and will not infer it from the inventory.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, apiUrl } from "./client";
import { refusalFrom } from "./refusal";
import type { components } from "./schema";
import { unwrap } from "./queries";
import type { Scope2Method } from "./queries";

export type SubmissionPack = components["schemas"]["SubmissionPackOut"];
export type SubmissionField = components["schemas"]["SubmissionFieldOut"];
export type SubmissionOption = components["schemas"]["SubmissionOptionOut"];
export type SubmissionRoute = components["schemas"]["SubmissionRouteOut"];
export type SubmissionNote = components["schemas"]["SubmissionNoteOut"];
export type Letter = components["schemas"]["LetterOut"];
export type Alternative = components["schemas"]["AlternativeOut"];

const packKey = (obligationId: string, method: Scope2Method) =>
  ["report", "submission-pack", obligationId, method] as const;

export function useSubmissionPack(obligationId: string | undefined, method: Scope2Method) {
  return useQuery({
    queryKey: packKey(obligationId ?? "", method),
    queryFn: async () =>
      unwrap(
        await api.GET("/api/v1/reports/obligations/{obligation_id}/submission-pack", {
          params: {
            path: { obligation_id: obligationId as string },
            query: { scope2_method: method },
          },
        }),
      ),
    enabled: Boolean(obligationId),
  });
}

/** Where the pack, written into the regulator's own template layout, is downloaded from.
 *
 * A plain link and not a query: the server answers with a file (`Content-Disposition`), and the
 * download is a view drawn on request, never stored. The Scope 2 method is the one on screen, so
 * the file states the figures the person is looking at. */
export function submissionExportUrl(obligationId: string, method: Scope2Method): string {
  return apiUrl(
    `/api/v1/reports/obligations/${obligationId}/submission-pack/export?scope2_method=${method}`,
  );
}

export interface LetterInput {
  optionKey: string;
  signatoryName: string;
  signatoryTitle: string;
  /** The person says the fact is true. Never defaulted to true by a screen. */
  confirmed: boolean;
  /** The person's own edit of the drafted words, if they made one. */
  text?: string;
}

export function useDraftLetter(obligationId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: LetterInput): Promise<Letter> => {
      const { data, error, response } = await api.PUT(
        "/api/v1/reports/obligations/{obligation_id}/submission-pack/letters/{option_key}",
        {
          params: { path: { obligation_id: obligationId, option_key: input.optionKey } },
          body: {
            confirmed: input.confirmed,
            signatory_name: input.signatoryName,
            signatory_title: input.signatoryTitle,
            text: input.text ?? null,
          },
        },
      );
      if (data === undefined || error !== undefined) {
        throw refusalFrom(response, error, "That statement could not be drafted.");
      }
      return data;
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["report", "submission-pack", obligationId] }),
  });
}
